import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import BrandsTemplate from "templates/lookups/brands";
import {
  activateBrandRequest,
  createBrandRequest,
  deactivateBrandRequest,
  deleteBrandRequest,
  getBrandDetailsRequest,
  getBrandsRequest,
  updateBrandRequest,
} from "services/modules/lookups/brands";

function BrandsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  let [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const list = useSelector((state) => state.brands.brands);
  const count = useSelector((state) => state.brands.count);
  const details = useSelector((state) => state.brands.brand);
  const load = useSelector((state) => state.brands.load);

  const currentUser = useSelector(
    (state) => state.authenticate.currentUserProfile,
  );
  const permissions = useSelector(
    (state) => state.authenticate.currentUserProfile.permissions?.brands,
  );
  const isSuperAdmin = currentUser?.privilege == "super_admin";

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState(null);

  const handlePageChange = (page) => {
    setPage(page);
    setFilterObj((prev) => {
      return { ...prev, page_number: page };
    });
  };

  const getDetails = async () => {
    dispatch(getBrandDetailsRequest({ brand_id: id }));
  };

  const getList = async (filterObj) => {
    const requestData = {
      params: {
        page_size: 10,
        page_number: 1,
        keyword: "",
        sort_by: "id",
        ...filterObj,
      },
    };
    dispatch(getBrandsRequest(requestData));
  };

  const mappedData = list?.map((data) => {
    const nameEn =
      data?.localized_data?.en?.name || data?.name_en || data?.name || "";
    const nameAr = data?.localized_data?.ar?.name || data?.name_ar || "";
    const imageSrc = data?.image;

    return {
      ...data,
      name_en: nameEn,
      name_ar: nameAr,
      products_count: data?.products_count ?? 0,
      created_at: <>{dayjs(data?.created_at).format("DD-MM-YYYY hh:mm")}</>,
      updated_at: <>{dayjs(data?.updated_at).format("DD-MM-YYYY hh:mm")}</>,
      display_priority: data?.display_priority ?? "-",
      image_url: imageSrc ? (
        <img src={imageSrc} alt={nameEn} width="125px" height="77px" />
      ) : (
        t("chat.no_image")
      ),
      is_active_label: (
        <>{data?.is_active ? t("common.yes") : t("common.no")}</>
      ),
    };
  });

  const rowData = (data) => {
    setSelectedRow(data);
  };

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("common.home"),
    },
    {
      active: !type ? false : true,
      path: "/brands",
      title: t("lookups_brands.title"),
    },
    type && {
      active: false,
      title:
        type === "details"
          ? t("lookups_brands.details")
          : id
            ? t("lookups_brands.edit")
            : t("lookups_brands.add"),
    },
  ];

  const handelSubmit = (data, formik) => {
    const action = () => {
      searchParams.delete("id");
      searchParams.delete("type");
      navigate("/brands");
      getList(filterObj);
      !id && formik?.resetForm();
      toast.success(
        id ? t("lookups_brands.updated") : t("lookups_brands.created"),
      );
    };
    const requestData = {
      body: {
        brand: {
          name_en: formik?.values?.name_en,
          name_ar: formik?.values?.name_ar,
          image: data?.image,
          display_priority: formik?.values?.display_priority
            ? Number(formik?.values?.display_priority)
            : null,
        },
      },
      action,
    };

    if (id) {
      dispatch(updateBrandRequest({ ...requestData, id }));
    } else {
      dispatch(createBrandRequest(requestData));
    }
  };

  const filterList = [
    {
      type: "filter",
      placeholder: t("lookups_brands.is_active"),
      onChange: (value) => {
        setFilterObj((s) => {
          return { ...s, is_active: value?.value, page_number: 1 };
        });
      },
      options: [
        { id: true, name: t("common.yes") },
        { id: false, name: t("common.no") },
      ],
    },
  ];

  const handleDelete = (id) => {
    const action = () => {
      if ((list.length === 1) & (page !== 1)) {
        setPage(page - 1);
        setFilterObj({
          ...filterObj,
          page_number: page - 1,
        });
        getList({ ...filterObj, page_number: page - 1 });
      } else {
        getList(filterObj);
      }
      type === "details" && navigate("/brands");
      toast.success(t("lookups_brands.deleted"));
    };
    const requestData = {
      id,
      action,
    };
    dispatch(deleteBrandRequest(requestData));
    setOpenDeleteModal(false);
  };

  const handleToggleActive = (data) => {
    const isActive = data?.is_active === true;
    const action = () => {
      toast.success(
        isActive
          ? t("lookups_brands.deactivated")
          : t("lookups_brands.activated"),
      );
      getList(filterObj);
    };
    const requestData = { id: data?.id, action };
    if (isActive) {
      dispatch(deactivateBrandRequest(requestData));
    } else {
      dispatch(activateBrandRequest(requestData));
    }
  };

  const actionsList = ({ data }) => {
    const isActive = data?.is_active === true;
    return [
      {
        condition: () => {
          return isSuperAdmin || permissions?.can_update;
        },
        click: () => {
          navigate(`?type=form&id=${data?.id}`);
        },
        text: t("common.edit"),
      },
      {
        condition: () => {
          return isSuperAdmin || permissions?.can_update;
        },
        click: () => {
          handleToggleActive(data);
        },
        text: isActive
          ? t("lookups_brands.deactivate")
          : t("lookups_brands.activate"),
      },
      {
        condition: () => {
          return isSuperAdmin || permissions?.can_delete;
        },
        click: () => {
          setOpenDeleteModal(true);
        },
        text: t("common.delete"),
      },
    ];
  };

  const headerChildren = <></>;
  const modalComponents = [];

  useEffect(() => {
    if (filterObj) getList(filterObj);
  }, [filterObj]);

  useEffect(() => {
    if (id) getDetails();
  }, [id]);

  return (
    <>
      <Loader open={load?.loading && load?.type == "list"} />
      <BrandsTemplate
        filterObj={filterObj}
        setFilterObj={setFilterObj}
        data={mappedData}
        count={count}
        paginationPage={page}
        handlePageChange={handlePageChange}
        rowData={rowData}
        details={details}
        breadcrumbs={breadcrumbs}
        filterList={filterList}
        handelSubmit={handelSubmit}
        actionsList={actionsList}
        headerChildren={headerChildren}
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        handleDeleteRequest={handleDelete}
        modalComponents={modalComponents}
        can_create={isSuperAdmin || permissions?.can_create}
      />
    </>
  );
}

export default BrandsPage;
